import {getTestProps} from '@benchling/js-test-utils';
import {createStyles, Stack, Tabs, Text} from '@mantine/core';
import React from 'react';

import ChatSettingsListItem from 'llm/chat-library/shared/list-detail/ChatSettingsListItem';
import ChatSettingsPane from 'llm/chat-library/shared/list-detail/ChatSettingsPane';
import {ListGroup, ListingViewHeader} from 'llm/chat-library/shared/list-detail/listDetailTypes';
import {CenteredSpinner} from 'ui-library/Spinner';

const useStyles = createStyles((theme) => ({
  tabsRoot: {
    display: 'flex',
    flexDirection: 'column',
    flex: '1 1 auto',
    minHeight: 0,
    overflow: 'hidden',
  },
  tabsList: {
    // Each tab already has md of horizontal padding, so this remainder lines the label up with the 3xl inset.
    paddingTop: theme.spacing.none,
    paddingBottom: theme.spacing.none,
    paddingLeft: `calc(${theme.spacing['3xl']} - ${theme.spacing.md})`,
    paddingRight: `calc(${theme.spacing['3xl']} - ${theme.spacing.md})`,
  },
  tab: {
    padding: theme.spacing.md,
    '&[data-active]': {
      borderBottomWidth: 3,
    },
  },
  scroll: {
    flex: '1 1 auto',
    minHeight: 0,
    overflowY: 'auto',
    paddingTop: theme.spacing.xxl,
    paddingBottom: theme.spacing.md,
    paddingLeft: theme.spacing['3xl'],
    paddingRight: theme.spacing['3xl'],
  },
  empty: {
    paddingTop: theme.spacing.xxl,
    paddingLeft: theme.spacing['3xl'],
    paddingRight: theme.spacing['3xl'],
    textAlign: 'center',
  },
}));

export default function ChatSettingsListingView({
  header,
  groups,
  activeGroupId,
  onGroupChange,
  isLoading = false,
  emptyMessage = 'Nothing to show yet.',
  selectedItemId,
  onSelectItem,
}: {
  header?: ListingViewHeader;
  groups: Array<ListGroup>;
  /** The selected group's tab. Defaults to the first group when unset. */
  activeGroupId?: string;
  onGroupChange?: (groupId: string) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  selectedItemId?: string | null;
  onSelectItem?: (id: string) => void;
}): JSX.Element {
  const {classes} = useStyles();

  // Groups always render as a horizontal tab bar; fall back to the first group so a tab is always
  // active even when the caller doesn't control the selection.
  const selectedGroupId = activeGroupId ?? groups[0]?.id;
  const activeGroup = groups.find((group) => group.id === selectedGroupId);

  return (
    <ChatSettingsPane header={header} scrollable={false} wideInset={true}>
      <Tabs
        value={selectedGroupId}
        onTabChange={(value) => {
          if (value != null) {
            onGroupChange?.(value);
          }
        }}
        keepMounted={false}
        className={classes.tabsRoot}
      >
        <Tabs.List className={classes.tabsList}>
          {groups.map((group, index) => (
            <Tabs.Tab
              key={group.id ?? index}
              value={group.id ?? ''}
              className={classes.tab}
              {...getTestProps({component: 'ChatSettingsListingView', element: 'tab', key: group.id})}
              data-amp-safe={false}
            >
              {group.header}
            </Tabs.Tab>
          ))}
        </Tabs.List>
        <Tabs.Panel value={selectedGroupId ?? ''} className={classes.tabsRoot}>
          {isLoading ? (
            <CenteredSpinner />
          ) : activeGroup == null || activeGroup.items.length === 0 ? (
            <Text className={classes.empty} c="dimmed">
              {emptyMessage}
            </Text>
          ) : (
            <Stack spacing="lg" className={classes.scroll}>
              <Stack spacing="md">
                {activeGroup.items.map((item) => (
                  <ChatSettingsListItem
                    key={item.id}
                    item={item}
                    selected={item.id === selectedItemId}
                    onClick={
                      onSelectItem != null
                        ? () => {
                            onSelectItem(item.id);
                          }
                        : undefined
                    }
                  />
                ))}
              </Stack>
            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>
    </ChatSettingsPane>
  );
}
